import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ContributeButton from '../../../../../components/global/ContributeButton/ContributeButton';
import useOpenModal from '../../../../../hooks/useOpenModal';
import { getThumbBySize } from '../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import { VIDEO_TYPE } from '../../../../../constants/videos';
import { ALBUM_SUBTYPE } from '../../../../../constants/albums';
import { isAlbumOwner } from '../../../../../util/account';
import { isVideoSlideshowAvailable } from '../../../../../util/album';
import ListingCardTemplate from '../../../../pro/components/ListingCardTemplate';
import PlayButtonIcon from '../../../../../components/icons/PlayButtonIcon';
import Button from '../../../../../components/global/Button';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../constants/modals';

const propTypes = {
  linkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  className: PropTypes.string,
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
  ),
  thumbnailActions: PropTypes.node,
  title: PropTypes.string,
};

const WebGalleryVideoGuestbookListingCard = ({
  linkTo,
  onClick,
  user,
  album,
  thumbnails,
  className,
  thumbnailActions,
  title,
}) => {
  const openModal = useOpenModal();
  const videosRecords = album?.videos?.records ?? [];
  const hasUploadedVideo = (album?.user_uploaded_videos?.total ?? 0) > 0;
  const greetingVideo = videosRecords.find(
    video => video.type === VIDEO_TYPE.GREETING,
  );

  const thumbnailUrl = getThumbBySize(
    thumbnails ?? greetingVideo?.thumbnails ?? [],
    IMAGE_SIZE_CODE.MEDIUM2X,
    // Important for when using local cache
    true,
  )?.url;

  const classes = classNames({
    'album-card': true,
    'video-guestbook-card': true,
    'with-thumbnail': !!thumbnailUrl,
    [className]: !!className,
  });

  const buttonContainerClasses = classNames({
    'listing-card-template-cover-null-state-actions-buttons': true,
  });
  const isMemorials = album?.album_subtype === ALBUM_SUBTYPE.MEMORIALS;
  const isOwner = isAlbumOwner(user, album);
  const showPlayIcon = !isOwner && isVideoSlideshowAvailable(album);

  return (
    <ListingCardTemplate
      className={classes}
      linkTo={linkTo}
      onClick={onClick}
      title={title ?? album?.name}
      thumbnailUrl={thumbnailUrl}
      thumbnailActions={thumbnailActions}
      info={showPlayIcon ? <PlayButtonIcon size={80} /> : undefined}
      note={
        <Fragment>
          {!thumbnailUrl && (
            <Fragment>
              {isOwner && (
                <p>
                  Gather 30-second{' '}
                  {isMemorials
                    ? 'video messages for the family'
                    : 'well wishes from your guests!'}
                </p>
              )}
              {!isOwner && <p>View others&rsquo; well wishes and add yours!</p>}
            </Fragment>
          )}

          <div className={buttonContainerClasses}>
            <ContributeButton
              albumId={album?.id}
              btnBlock={!!thumbnailUrl}
              className="text-uppercase"
              btnStyle={thumbnailUrl ? 'white' : 'primary'}
              btnOutline
              btnSize={thumbnailUrl ? 'sm' : 'xs'}
            >
              {isOwner
                ? `${greetingVideo ? 'Edit' : 'Add'} Greeting`
                : `${hasUploadedVideo ? 'Edit' : 'Add'} My Message`}
            </ContributeButton>
            <Button
              btnBlock={!!thumbnailUrl}
              className="text-uppercase"
              btnStyle={thumbnailUrl ? 'white' : 'primary'}
              btnOutline
              btnSize={thumbnailUrl ? 'sm' : 'xs'}
              onClick={event => {
                event.preventDefault();
                openModal(CONTRIBUTOR_SHARE_MODAL);
              }}
            >
              Share
            </Button>
          </div>
        </Fragment>
      }
    />
  );
};

WebGalleryVideoGuestbookListingCard.propTypes = propTypes;

export default WebGalleryVideoGuestbookListingCard;
